import { useContext, useEffect, useMemo, useState } from "react";
import ProgressSpinner from "../components/Common/ProgressSpinner";
import queryString from "query-string";
import { getUserAttributes, refreshCurrentSession } from "../utils/auth";
import { GlobalContext } from "../context/GlobalContext";
import { useNavigate } from "react-router-dom";
import Suggestion from "../components/Integrations/suggestion";
import UserLevelSettings from "../components/Integrations/UserLevelSettings";
import { Tabs, Tab, Box, Typography } from "@mui/material";
import CustomButton from "../components/Common/Button";
// import Integration from "../components/Integrations/Integration";
import Spaces from "../components/Integrations/Spaces";
import Tasks from "../components/Integrations/Tasks";
import Extension from "../components/Integrations/Extension";
import AppConfigForm from "../components/Integrations/AppConfigForm";
import useDisplaySnackbar from "../utils/useDisplaySnackbar";
import FilterPill from "../components/Common/FilterPill";
import { capitalizeFirstLetter, getActiveAccounts, relationshipTypeOptions, options } from "../utils/utils";
import IntegrationService from "../services/integrations/Integrations";
import UserInteractionsService from "../services/interactions/Interactions";
import Integration from "../components/Integrations/Integration";
import OrgManagement from "../components/Integrations/OrgManagement";
import UserPreference from "../components/Integrations/UserPrefernce";
import VideoGuides from "../components/Integrations/VideoGuides";
import { DateRangeValue, Options } from "../utils/types";
import CommonService from "../services/common/commonService";
import SearchIcon from "../assets/common/search-icon.svg";
import CloseIcon from "@mui/icons-material/Close";

type TabItem = {
  value: (typeof TAB_VALUES)[keyof typeof TAB_VALUES];
  label: string;
};
  const TAB_VALUES = {
    INTEGRATION: "INTEGRATION",
    USER: "USER_LEVEL_SETTINGS",
    CUSTOM: "CUSTOM_TOPIC_TRACK",
    SPACES: "SPACES",
    TASKS: "TASKS",
    EXTENSION: "EXTENSION",
    APP_CONFIG: "APP_CONFIG",
    ORG_MANAGEMENT: "ORG_MANAGEMENT",
    CONTROLS: "CONTROLS",
    VIDEO_GUIDES: "VIDEO_GUIDES"
  } as const;

const IntegrationsPage = () => {
  const { adminScreen, accountDetails, featureMap } = useContext(GlobalContext);
  const navigate = useNavigate();
  const pathname = location.pathname;
  const loggedInEmail = getUserAttributes()?.email;
  const is7thGearUser = loggedInEmail && loggedInEmail.endsWith("@7thgear.ai");
  const isHighRadiusUser = loggedInEmail?.endsWith("@highradius.com");
  const isCalendarIntegrationsEnabled = featureMap?.calendar_integrations === true;
  const userPreferencesEnabled = featureMap?.user_preferences === true;
  const USER_TABS = useMemo<TabItem[]>(() => {
    const tabs: TabItem[] = [];

    tabs.push({ value: TAB_VALUES.USER, label: "User Settings" });

    if (isCalendarIntegrationsEnabled) {
      tabs.push({
        value: TAB_VALUES.INTEGRATION,
        label: "Integrations",
      });
    }

    tabs.push(
      { value: TAB_VALUES.CUSTOM, label: "Custom Topics To Track" },
      { value: TAB_VALUES.EXTENSION, label: "Extension Link" },
    );
    if (userPreferencesEnabled) {
      tabs.push({
        value: TAB_VALUES.CONTROLS,
        label: "Preference Controls",
      });
    }

    return tabs;
  }, [isHighRadiusUser, is7thGearUser, isCalendarIntegrationsEnabled, userPreferencesEnabled]);
    const NOTIFICATION_TABS = [
      { value: TAB_VALUES.TASKS, label: "Tasks" },
    ];

  const SPACE_TABS = [
    {value :TAB_VALUES.SPACES, label: "Spaces" },
  ];

  const APP_CONFIG_TABS = [
    { value: TAB_VALUES.APP_CONFIG, label : "Account Config" },
  ];

  const ORG_MANAGEMENT_TABS = [
    { value: TAB_VALUES.ORG_MANAGEMENT, label : "Domain Management" },
  ];

  const VIDEO_GUIDES_TABS = [
    { value: TAB_VALUES.VIDEO_GUIDES, label: "Video Guides" },
  ];

  // final tab list based on URL
  const AVAILABLE_TABS = useMemo(() => 
    pathname.includes("notification")
      ? NOTIFICATION_TABS
      : pathname.includes("space") 
      ? SPACE_TABS
      : pathname.includes("config")
      ? APP_CONFIG_TABS
      : pathname.includes("org")
      ? ORG_MANAGEMENT_TABS
      : pathname.includes("video")
      ? VIDEO_GUIDES_TABS
      : USER_TABS
  , [pathname]);

  useEffect(() => {
    const queryParamString = queryString.parse(location.search);
    const tabFromQuery = queryParamString["tab"];

    if (tabFromQuery && AVAILABLE_TABS.some(t => t.value === tabFromQuery)) {
      setSelectedTab(tabFromQuery as string);
    } else {
      setSelectedTab(AVAILABLE_TABS[0].value);
    }
  }, [pathname, AVAILABLE_TABS, location.search]);
  const queryParamString = queryString.parse(location.search);
  const integrationStatus = queryParamString["success"];
  const [showSpinner, setShowSpinner] = useState<boolean>(false);
  const [selectedTab, setSelectedTab] = useState<string>(AVAILABLE_TABS[0].value);
  const [searchQuery, setSearchQuery] = useState("");
  const [userSettingsData, setUserSettingsData] = useState<any>(null);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [userSettingsPageNumber, setUserSettingsPageNumber] = useState(1);
  const [userSettingsPageSize, setUserSettingsPageSize] = useState(50);
  const [noteTakerFilter, setNoteTakerFilter] = useState("");
  const [calendarFilter, setCalendarFilter] = useState("");
  const roleId = accountDetails?.roleId;
  const isAdmin = roleId?.toLowerCase() === "admin" || roleId?.toLowerCase() === "superaccess";
  const [clientList, setClientList] = useState<any[]>([]);
  const [selectedClient, setSelectedClient] = useState<any>(null);
  const [orgDomainFilter, setOrgDomainFilter] = useState("");
  const [relationshipTypeFilter, setRelationshipTypeFilter] = useState("");
  const [accountNameFilter, setAccountNameFilter] = useState("");
  const [accountManagerFilter, setAccountManagerFilter] = useState<string[]>([]);
  const [onboardedDateRange, setOnboardedDateRange] = useState<DateRangeValue | undefined>();
  const [offboardedDateRange, setOffboardedDateRange] = useState<DateRangeValue | undefined>();
  const [clientType, setClientType] = useState<string[]>([]);
  const [clientTypeOptions, setClientTypeOptions] = useState<Options[]>([]);
  const { showSnackbar } = useDisplaySnackbar();
  const [sort, setSort] = useState<{
    sort_order: "asc" | "desc";
    }>({
    sort_order: "asc",
  });
  const isSingle = roleId?.toLowerCase() === "user";
  const { data: activeUsersData } = UserInteractionsService.useGetActiveUsers({}, true);

  const accountManagerOptions = useMemo<Options[]>(() => {
    const activeUsers = activeUsersData?.data?.data || [];

    return activeUsers
      .filter((user: any) => Boolean(user?.email))
      .map((user: any) => ({
        label: user.name || user.email || "-",
        value: user.email,
      }));
  }, [activeUsersData]);

  const {
    isSuccess: isOrgSummaryFiltersSuccess,
    isError: isOrgSummaryFiltersError,
    error: orgSummaryFiltersError,
    data: orgSummaryFiltersData,
  } = CommonService.useGetOrgSummaryFilters(true);

  useEffect(() => {
    if (isOrgSummaryFiltersSuccess && orgSummaryFiltersData) {
      const { clientTypes = [], } = orgSummaryFiltersData;

      setClientTypeOptions(
        clientTypes.map((type: string) => ({
          label: capitalizeFirstLetter(type),
          value: type,
        }))
      );
    }

    if (isOrgSummaryFiltersError) {
      showSnackbar("error", orgSummaryFiltersError?.message || "Failed to load filter options.");
    }
  }, [isOrgSummaryFiltersSuccess, orgSummaryFiltersData, isOrgSummaryFiltersError, orgSummaryFiltersError]);

  const hasDateRange = (range?: DateRangeValue) =>
    Boolean(range?.startDate || range?.endDate || range?.preset);

  const formatDateRange = (range?: DateRangeValue) => {
    if (!range?.startDate && !range?.endDate) {
      return "";
    }

    const format = (date: string) =>
      new Date(date).toLocaleDateString("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric",
      });

    if (range.startDate && range.endDate) {
      return `${format(range.startDate)} - ${format(range.endDate)}`;
    }

    return range.startDate ? format(range.startDate) : range.endDate ? format(range.endDate) : "";
  };

  const orgDatePresetOptions = [{ label: "Custom", value: "custom" }];

  const showOrgManagementFilters =
    selectedTab === TAB_VALUES.ORG_MANAGEMENT &&
    (
      Boolean(orgDomainFilter.trim()) ||
      Boolean(accountNameFilter.trim()) ||
      Boolean(relationshipTypeFilter) ||
      Boolean(accountManagerFilter.length) ||
      Boolean(clientType.length) ||
      hasDateRange(onboardedDateRange) ||
      hasDateRange(offboardedDateRange)
    );

    const showUserSettingsFilters = 
      selectedTab === TAB_VALUES.USER && 
      (Boolean(noteTakerFilter) || Boolean(calendarFilter));

  const {
    isLoading: isAccountsLoading,
    isFetching: isAccountsFetching,
    isError: isAccountsError,
    error: accountsError,
    data: AccountsData,
  } = IntegrationService.useGetActiveAccounts(true);

  const {
    isLoading: isUserSettingsLoading,
    isFetching: isUserSettingsFetching,
    isSuccess: isUserSettingsParentSuccess,
    isError: isUserSettingsParentError,
    error: userSettingsParentError,
    data: userSettingsParentData,
  } = IntegrationService.useGetUserLevelSettings(
    {
      offset: userSettingsPageNumber,
      limit: userSettingsPageSize,
      employeeEmail: loggedInEmail || "",
      ...(debouncedSearch ? { fullName: debouncedSearch } : {}),
      ...(calendarFilter === "enable"
        ? { syncEnabled: true }
        : calendarFilter === "disable"
        ? { syncEnabled: false }
        : {}),
      ...(noteTakerFilter === "enable"
        ? { botSchedulingDisabled: false }
        : noteTakerFilter === "disable"
        ? { botSchedulingDisabled: true }
        : {}),
        sortOrder: sort.sort_order,
    },
    isCalendarIntegrationsEnabled || selectedTab === TAB_VALUES.USER
  );

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
    }, 500);

    return () => clearTimeout(handler);
  }, [searchQuery]);

  useEffect(() => {
    setUserSettingsPageNumber(1);
  }, [debouncedSearch, noteTakerFilter, calendarFilter]);

  useEffect(() => {
    if (isUserSettingsParentSuccess) {
      setUserSettingsData(userSettingsParentData);
    }
    if (isUserSettingsParentError) {
      showSnackbar("error", userSettingsParentError?.message || userSettingsParentError);
    }
  }, [
    isUserSettingsParentSuccess,
    isUserSettingsParentError,
    userSettingsParentData,
    userSettingsParentError,
  ]);

  useEffect(() => {
    if (AccountsData) {
      const AccountsList = AccountsData?.data || [];
      setClientList(AccountsList);
    } else if (isAccountsError) {
      showSnackbar("error", accountsError?.message || "Failed to load data.");
    }
  }, [AccountsData, isAccountsError, accountsError, isAccountsLoading, isAccountsFetching]);

  useEffect(() => {
    if (clientList.length > 0 && !selectedClient) {
      const defaultAccount = clientList.find(
        (acc) => acc.accountId === "5c66b4c3-cfc1-46f8-ac6e-75fc4d0534ae"
      );

      if (defaultAccount) {
        setSelectedClient(defaultAccount.accountId);
      }
    }
  }, [clientList, selectedClient]);

  useEffect(() => {
    const updateAccessToken = async () => {
      try {
        if (integrationStatus && integrationStatus === "1") {
          setShowSpinner(true);
          await refreshCurrentSession();
        }
      } finally {
        setShowSpinner(false);
      }
    };
    updateAccessToken();
  }, [integrationStatus]);
  

  return (
    <>
      {selectedTab === TAB_VALUES.APP_CONFIG && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
          }}
        >
          <FilterPill
            key="account"
            selectedTab={selectedTab}
            label="Account"
            value={selectedClient}
            options={getActiveAccounts({ accountList: clientList })}
            onChange={(val: any) => setSelectedClient(val)}
            type="select"
            parent_page="settings_page"
          />
        </Box>
      )}
      {selectedTab === TAB_VALUES.ORG_MANAGEMENT && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            gap: "8px",
            flexWrap: "wrap",
          }}
        >
          {showOrgManagementFilters && (
            <>
            {/* Domain */}
            {Boolean(orgDomainFilter.trim()) && (
              <FilterPill
                key="domain"
                selectedTab={selectedTab}
                label="Domain"
                value={orgDomainFilter}
                onChange={(value) =>
                  setOrgDomainFilter(typeof value === "string" ? value.trim() : "")
                }
                type="text"
                parent_page="settings_page"
              />
            )}

            {/* Account Manager */}
            {Boolean(accountManagerFilter.length) && (
              <FilterPill
                key="accountManager"
                selectedTab={selectedTab}
                label="Account Manager"
                value={accountManagerFilter}
                options={accountManagerOptions}
                displayValue={
                  accountManagerFilter
                    .map((manager) => accountManagerOptions.find((option) => option.value === manager)?.label || manager)
                    .join(", ")
                }
                onChange={(value) =>
                  setAccountManagerFilter(Array.isArray(value) ? value.filter(Boolean) : [])
                }
                type="select"
                parent_page="settings_page"
              />
            )}

            {/* Onboarded Date */}
            {hasDateRange(onboardedDateRange) && (
              <FilterPill
                key="onboardedDate"
                selectedTab={selectedTab}
                label="Onboarded Date"
                type="date"
                value={onboardedDateRange}
                displayValue={formatDateRange(onboardedDateRange)}
                datePresetOptions={orgDatePresetOptions}
                onChange={(value) => {
                  if (
                    !value ||
                    typeof value === "string" ||
                    Array.isArray(value) ||
                    !("preset" in value)
                  ) {
                    setOnboardedDateRange(undefined);
                    return;
                  }

                  setOnboardedDateRange(value);
                }}
                parent_page="settings_page"
              />
            )}

            {/* Off boarding Date */}
            {hasDateRange(offboardedDateRange) && (
              <FilterPill
                key="offboardedDate"
                selectedTab={selectedTab}
                label="Off boarding Date"
                type="date"
                value={offboardedDateRange}
                displayValue={formatDateRange(offboardedDateRange)}
                datePresetOptions={orgDatePresetOptions}
                onChange={(value) => {
                  if (
                    !value ||
                    typeof value === "string" ||
                    Array.isArray(value) ||
                    !("preset" in value)
                  ) {
                    setOffboardedDateRange(undefined);
                    return;
                  }

                  setOffboardedDateRange(value);
                }}
                parent_page="settings_page"
              />
            )}

            {/* Account Name */}
            {Boolean(accountNameFilter.trim()) && (
              <FilterPill
                key="accountName"
                selectedTab={selectedTab}
                label="Account Name"
                value={accountNameFilter}
                onChange={(value) =>
                  setAccountNameFilter(typeof value === "string" ? value.trim() : "")
                }
                type="text"
                parent_page="settings_page"
              />
            )}

            {/* Relationship Type */}
            {Boolean(relationshipTypeFilter) && (
              <FilterPill
                key="relationshipType"
                selectedTab={selectedTab}
                label="Relationship Type"
                value={relationshipTypeFilter}
                onChange={(value) =>
                  setRelationshipTypeFilter(
                    typeof value === "string" ? value.trim() : ""
                  )
                }
                type="select"
                options={[relationshipTypeOptions].flat()}
                parent_page="settings_page"
              />
            )}

            {Boolean(clientType.length) && (
              <FilterPill
                key="clientType"
                selectedTab={selectedTab}
                label="Customer Type"
                value={clientType}
                displayValue={clientType
                  .map((type) => clientTypeOptions.find((option) => option.value === type)?.label || type)
                  .join(", ")}
                onChange={(value) =>
                  setClientType(Array.isArray(value) ? value.filter(Boolean) : [])
                }
                type="select"
                options={clientTypeOptions}
                parent_page="settings_page"
              />
            )}
            </>
          )}
        </Box>
      )}
      {showUserSettingsFilters && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            gap: "8px",
            flexWrap: "wrap",
          }}
        >
          {Boolean(noteTakerFilter) && (
            <FilterPill
              key="notetaker"
              type="select"
              label="Notetaker"
              options={options}
              value={noteTakerFilter}
              onChange={(value: any) =>
                setNoteTakerFilter(typeof value === "string" ? value : Array.isArray(value) ? value[0] || "" : "")
              }
              showClearIcon={true}
              useColumnFilter
              columnKey="analyzer"
              parent_page="settings_page"
              singleSelection={true}
            />
          )}
          {Boolean(calendarFilter) && (
            <FilterPill
              key="calendar"
              type="select"
              label="Calendar"
              options={options}
              value={calendarFilter}
              onChange={(value: any) =>
                setCalendarFilter(typeof value === "string" ? value : Array.isArray(value) ? value[0] || "" : "")
              }
              showClearIcon={true}
              useColumnFilter
              columnKey="calendar"
              parent_page="settings_page"
              singleSelection={true}
            />
          )}
        </Box>
      )}
      <Box
        className="integration-box"
        sx={{
          height:
            selectedTab === TAB_VALUES.APP_CONFIG || showOrgManagementFilters || showUserSettingsFilters
              ? "90%"
              : "100%",
        }}
      >
      <Box sx={{ display: "inline-flex" }} className="w-full items-start justify-between integration__titles">
        <Tabs
          value={selectedTab}
          onChange={(_event: any, newValue) => {
            setSelectedTab(newValue);
            setSearchQuery(""); // clear search on tab switch
            setUserSettingsPageNumber(1);
            setOrgDomainFilter("");
            setAccountNameFilter("");
            setRelationshipTypeFilter("");
            setAccountManagerFilter([]);
            setClientType([]);
            setOnboardedDateRange(undefined);
            setOffboardedDateRange(undefined);
          }}
          TabIndicatorProps={{
            sx: {
              backgroundColor:"#6036D6",
            },
          }}
          centered
        >
          {AVAILABLE_TABS.map((tab) => (
            <Tab
              key={tab.value}
              value={tab.value}
              label={
                <Typography
                  className="integration__tab-text"
                  sx={{
                    color: selectedTab === tab.value ? "#6036D6" : "#6F6F7F",
                    fontWeight: 600,
                  }}
                  >
                {tab.label}
                </Typography>
              }
            />
          ))}
        </Tabs>

        {selectedTab === TAB_VALUES.USER && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            marginRight: "20px",
            marginBottom: "10px",
          }}
        >
          {adminScreen && (
            <CustomButton
              type="SmallBrandGradient"
              clickHandler={() => navigate("/settings/admin")}
              label="Admin"
            />
          )}
          {!isSingle && (
            <Box className="search-box-container">
              <Box className="search-input-wrapper" sx={{ position: "relative" }}>
                <img src={SearchIcon} alt="" className="search-icon" />
                <input
                  type="text"
                  placeholder="Search by name"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="search-input"
                  style={{ paddingRight: "30px" }}
                />
                {searchQuery && (
                  <CloseIcon
                    sx={{
                      position: "absolute",
                      right: 5,
                      cursor: "pointer",
                      color: "#999",
                      fontSize: 20,
                    }}
                    onClick={() => setSearchQuery("")}
                  />
                )}
              </Box>
            </Box>
          )}
          </Box>
        )}
        {selectedTab === TAB_VALUES.SPACES && isAdmin && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              marginRight: "20px",
              marginBottom: "10px",
            }}
          >
            <button
              className="add-action-btn"
              onClick={() => {
                const event = new CustomEvent("openCreateSpace");
                window.dispatchEvent(event);
              }}
            >
              + Create Space
            </button>
          </Box>
        )}
      </Box>

      <Box>
        {selectedTab === TAB_VALUES.USER && (
          <UserLevelSettings
            userSettingsData={userSettingsData}
            isUserSettingsLoading={isUserSettingsLoading}
            isUserSettingsFetching={isUserSettingsFetching}
            noteTakerFilter={noteTakerFilter}
            setNoteTakerFilter={setNoteTakerFilter}
            calendarFilter={calendarFilter}
            setCalendarFilter={setCalendarFilter}
            onPageChange={(newPage) => setUserSettingsPageNumber(newPage + 1)}
            onPageSizeChange={(newPageSize) => {
              setUserSettingsPageSize(newPageSize);
              setUserSettingsPageNumber(1);
            }}
            sort={sort}
            setSort={setSort}
            searchQuery={searchQuery}
          />
        )}
        {selectedTab === TAB_VALUES.INTEGRATION && isCalendarIntegrationsEnabled && (
          <Integration
            userSettingsData={userSettingsData} isUserSettingsLoading={isUserSettingsLoading || isUserSettingsFetching} />
        )}
        {selectedTab === TAB_VALUES.CUSTOM && <Suggestion />}
        {selectedTab === TAB_VALUES.SPACES && <Spaces/>}
        {selectedTab === TAB_VALUES.TASKS && <Tasks/>}
        {selectedTab === TAB_VALUES.EXTENSION && 
        <Extension isHighRadiusUser={isHighRadiusUser} />}
        {selectedTab === TAB_VALUES.APP_CONFIG && (
          <Box
            sx={{
              height: "calc(100vh - 180px)",
              overflowY: "auto",
              pr: 1,
            }}
          >
            <AppConfigForm accountId={selectedClient} />
          </Box>
        )}
        {selectedTab === TAB_VALUES.ORG_MANAGEMENT && (
          <OrgManagement
            domainFilter={orgDomainFilter}
            onDomainFilterChange={setOrgDomainFilter}
            hasActiveDomainFilter={showOrgManagementFilters}
            relationshipTypeFilter={relationshipTypeFilter}
            setRelationshipTypeFilter={setRelationshipTypeFilter}
            accountNameFilter={accountNameFilter}
            onAccountNameFilterChange={setAccountNameFilter}
            accountManagerFilter={accountManagerFilter}
            setAccountManagerFilter={setAccountManagerFilter}
            onboardedDateRange={onboardedDateRange}
            setOnboardedDateRange={setOnboardedDateRange}
            offboardedDateRange={offboardedDateRange}
            setOffboardedDateRange={setOffboardedDateRange}
            activeUsers={activeUsersData?.data?.data ?? []}
            clientTypeOptions={clientTypeOptions}
            clientType={clientType}
            setClientType={setClientType}
          />
        )}
        {selectedTab === TAB_VALUES.CONTROLS && <UserPreference/>}
        {selectedTab === TAB_VALUES.VIDEO_GUIDES && <VideoGuides/>}
      </Box>

      <ProgressSpinner showSpinner={showSpinner} />
    </Box>
    </>
  );
};

export default IntegrationsPage;
